import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Calendar,
  MapPin,
  Palette,
  Ticket,
  FilePenLine,
  Sparkles,
  Plus,
  Settings2,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { toastPlanLimitError } from "@/lib/plan-limit";
import { usePersistedState } from "@/lib/persisted-state";
import { saveTicketEngine, syncTicketEngine } from "@/lib/ticket-engine";
import { createDefaultField } from "@/lib/form-builder";
import { API_BASE } from "@/lib/utils";
import { formatEventDateRange, normalizeEventDates } from "@/lib/event-dates";
import { createEmptyEventWizardDraft, isDemoEventWizardDraft } from "@/lib/event-wizard-defaults";
import { useAdminEvents } from "@/contexts/AdminEventsContext";
import { getAuthSession } from "@/lib/auth-session";

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";

const steps = [
  { key: "basics", label: "Basics", icon: Calendar },
  { key: "branding", label: "Branding", icon: Palette },
  { key: "tickets", label: "Tickets", icon: Ticket },
  { key: "form", label: "Form", icon: FilePenLine },
  { key: "review", label: "Review", icon: Sparkles },
];

/** Matches Form Builder field library for a consistent wizard → builder handoff. */
const FIELD_LIBRARY = [
  { type: "short_answer", label: "Short answer" },
  { type: "long_answer", label: "Long answer" },
  { type: "paragraph", label: "Paragraph text" },
  { type: "email", label: "Email" },
  { type: "phone", label: "Phone number" },
  { type: "number", label: "Number" },
  { type: "url", label: "Website URL" },
  { type: "date", label: "Date" },
  { type: "time", label: "Time" },
  { type: "datetime", label: "Date & time" },
  { type: "select", label: "Single select" },
  { type: "dropdown", label: "Dropdown" },
  { type: "multiselect", label: "Multi select" },
  { type: "radio", label: "Multiple choice (single)" },
  { type: "checkbox_group", label: "Multiple choice (multi)" },
  { type: "checkbox", label: "Checkbox" },
  { type: "yes_no", label: "Yes/No" },
  { type: "linear_scale", label: "Linear scale" },
  { type: "rating", label: "Rating" },
  { type: "nps", label: "Net promoter score (0-10)" },
  { type: "address", label: "Address" },
  { type: "country", label: "Country" },
  { type: "consent", label: "Consent / declaration" },
  { type: "section", label: "Section heading" },
  { type: "hidden", label: "Hidden field" },
  { type: "file", label: "File upload" },
];

function isoToday() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const ALL_TIMEZONES =
  typeof Intl !== "undefined" && typeof Intl.supportedValuesOf === "function"
    ? Intl.supportedValuesOf("timeZone")
    : ["UTC", "Asia/Singapore", "America/New_York", "Europe/London", "Australia/Sydney", "Europe/Berlin", "Asia/Tokyo"];

const OPTION_TYPES = new Set(["select", "dropdown", "multiselect", "checkbox_group", "radio", "yes_no", "linear_scale", "rating", "nps", "country"]);

function slugifyText(input) {
  return String(input || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function EventWizard() {
  const navigate = useNavigate();
  const { refreshEvents } = useAdminEvents();
  const [step, setStep] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const emptyDraft = useMemo(() => createEmptyEventWizardDraft(), []);
  const draftKey = useMemo(() => {
    const tenant = getAuthSession()?.tenant || "local";
    return `event-wizard.draft.${tenant}`;
  }, []);
  const { value: draft, setValue: setDraft, statusLabel } = usePersistedState(draftKey, emptyDraft);

  useEffect(() => {
    if (isDemoEventWizardDraft(draft)) {
      setDraft(emptyDraft);
    }
    try {
      localStorage.removeItem("aivoryems.state.event-wizard.draft");
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);
  const [slugAvailable, setSlugAvailable] = useState(true);

  const ensureSlug = (title, currentSlug) => {
    if (String(currentSlug || "").trim()) return String(currentSlug).trim().toLowerCase();
    const fromTitle = slugifyText(title);
    return fromTitle || `event-${Date.now().toString().slice(-6)}`;
  };

  const toFormApiFields = (fields) => {
    const rows = Array.isArray(fields) ? fields : [];
    return rows.map((f, idx) => ({
      id: String(f?.id || `f_${idx}`),
      key: String(f?.id || `f_${idx}`),
      type: String(f?.type || "short_answer"),
      label: String(f?.label || `Field ${idx + 1}`),
      required: Boolean(f?.required),
      options: Array.isArray(f?.options) ? f.options.map((x) => String(x).trim()).filter(Boolean) : [],
      helpText: String(f?.helpText || ""),
      placeholder: String(f?.placeholder || ""),
      ticketScope: [],
      visibilityRule: null,
      requiredRule: null,
      sortOrder: idx,
    }));
  };

  const skipTicketsStep = () => {
    setDraft((prev) => ({
      ...prev,
      tickets: [],
      tracks: [],
    }));
    setStep((s) => s + 1);
  };

  const publishEvent = async () => {
    const title = String(draft.title || "").trim();
    if (!title) {
      toast.error("Event title is required");
      return;
    }
    const slug = ensureSlug(title, draft.slug);
    const slugCheck = await fetch(
      `${API_BASE}/api/admin/events/slug-available?slug=${encodeURIComponent(slug)}`
    );
    if (slugCheck.ok) {
      const slugBody = await slugCheck.json();
      if (!slugBody?.available) {
        toast.error("Public URL unavailable", {
          description: "That slug is already in use. Choose a different slug on the Basics step.",
        });
        return;
      }
    }
    const { startDate, endDate } = normalizeEventDates(draft.startDate, draft.endDate);
    try {
      setPublishing(true);
      const subtitle = [String(draft.format || "").trim(), String(draft.subtitle || "").trim()].filter(Boolean).join(" · ");
      const res = await fetch(`${API_BASE}/api/admin/events`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          title,
          slug,
          format: draft.format || "In-person",
          subtitle,
          venue: draft.venue,
          timezone: draft.timezone,
          startDate,
          endDate,
          capacity: Number(draft.capacity || 0),
          status: "Selling",
          accent: draft.accent || "var(--primary)",
          secondaryAccent: draft.secondaryAccent || "",
          cover: draft.cover || "",
          logo: draft.logoUrl || "",
          registrationApproval: draft.registrationApproval || "auto_confirm",
        }),
      });
      if (res.status === 403) {
        const body = await res.json().catch(() => ({}));
        if (toastPlanLimitError(body.detail, res.status)) return;
        toast.error("Action blocked", { description: String(body?.detail || "Forbidden") });
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const detail = body?.detail;
        const message = typeof detail === "string" ? detail : Array.isArray(detail) ? detail[0]?.msg : null;
        if (res.status === 409) {
          toast.error("Public URL unavailable", {
            description: message || "That slug is already in use.",
          });
          return;
        }
        throw new Error(message || `Failed to publish event (${res.status})`);
      }
      const payload = await res.json();
      const eventId = payload?.event?.id;
      const eventTitle = payload?.event?.title || title;
      if (!eventId) throw new Error("Missing event id from publish response");

      const state = await syncTicketEngine(eventId);
      const nowIso = new Date().toISOString();
      const in30Iso = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
      const tiers = (Array.isArray(draft.tickets) ? draft.tickets : []).map((t, idx) => ({
        id: String(t.id || `tier_${idx}`),
        name: String(t.name || `Tier ${idx + 1}`),
        type: "TIER",
        price: Number(t.price || 0),
        sold: Number(t.sold || 0),
        quota: Number(t.quota || 0),
        status: String(t.status || "Selling"),
        purchaseLimitPerEmail:
          t.purchaseLimitPerEmail === null || t.purchaseLimitPerEmail === undefined || String(t.purchaseLimitPerEmail).trim() === "" || Number(t.purchaseLimitPerEmail) <= 0
            ? null
            : Math.max(1, Number(t.purchaseLimitPerEmail)),
        startsAt: t.startsAt || nowIso,
        endsAt: t.endsAt || in30Iso,
        allowCrossSell: true,
        trackCode: t.trackCode != null && String(t.trackCode).trim() ? String(t.trackCode).trim().toUpperCase().slice(0, 16) : null,
      }));
      const trackRows = (Array.isArray(draft.tracks) ? draft.tracks : []).map((tr, idx) => ({
        id: String(tr.id || `track_${idx}`),
        name: String(tr.name || `Track ${idx + 1}`),
        type: "TRACK",
        price: Number(tr.price ?? 0),
        sold: Number(tr.sold || 0),
        quota: Number(tr.quota ?? 0),
        status: String(tr.status || "Selling"),
        purchaseLimitPerEmail:
          tr.purchaseLimitPerEmail === null || tr.purchaseLimitPerEmail === undefined || String(tr.purchaseLimitPerEmail).trim() === ""
            ? 1
            : Math.max(1, Number(tr.purchaseLimitPerEmail)),
        startsAt: tr.startsAt || nowIso,
        endsAt: tr.endsAt || in30Iso,
        allowCrossSell: false,
        trackCode: (() => {
          const code = String(tr.trackCode || "").trim();
          if (code) return code.toUpperCase().slice(0, 16);
          const base = slugifyText(tr.name || `tr${idx}`).replace(/-/g, "_").toUpperCase();
          return base.slice(0, 16) || `TR${idx + 1}`;
        })(),
      }));
      let tiersOut = tiers;
      if (tiersOut.length === 0 && trackRows.length === 0) {
        tiersOut = [
          {
            id: `tier_ga_${Date.now()}`,
            name: "General Admission",
            type: "TIER",
            price: 0,
            sold: 0,
            quota: Math.max(1, Number(draft.capacity) || 500),
            status: "Selling",
            purchaseLimitPerEmail: null,
            startsAt: nowIso,
            endsAt: in30Iso,
            allowCrossSell: true,
            trackCode: null,
          },
        ];
      }
      await saveTicketEngine(eventId, {
        ...state,
        eventId,
        tickets: [...tiersOut, ...trackRows],
      });

      const formDraftRes = await fetch(`${API_BASE}/api/events/${eventId}/form-builder/draft`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          fields: toFormApiFields(draft.formFields),
          active: null,
          version: 1,
        }),
      });
      if (!formDraftRes.ok) {
        const body = await formDraftRes.json().catch(() => ({}));
        throw new Error(body?.detail || `Failed to save form draft (${formDraftRes.status})`);
      }

      await refreshEvents();
      setDraft(emptyDraft);
      toast.success("Event created", { description: `${eventTitle} is now in your events list` });
      navigate(`/admin/events/${eventId}`);
    } catch (err) {
      toast.error("Could not create event", { description: err?.message || "Unknown error" });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="min-h-full" data-testid="event-wizard">
      {/* Progress bar */}
      <div className="backdrop-blur-xl bg-[var(--bg-base-alpha80)] border-b border-[var(--border-default)]">
        <div className="max-w-[900px] mx-auto px-4 md:px-8 py-4 md:py-5 flex items-center gap-1 md:gap-2">
          {steps.map((s, i) => (
            <div key={s.key} className="flex items-center gap-1 md:gap-2 flex-1 pointer-events-none">
              <button
                onClick={() => setStep(i)}
                data-testid={`wizard-step-${s.key}`}
                className={`pointer-events-auto flex items-center gap-1.5 md:gap-2 text-xs font-mono uppercase tracking-widest transition min-h-[2.75rem] ${
                  i === step ? "text-[var(--primary)]" : i < step ? "text-[var(--text-primary)]" : "text-[var(--text-tertiary)]"
                }`}
              >
                <span className={`w-6 h-6 border flex items-center justify-center shrink-0 ${i === step ? "border-[var(--primary)] bg-[var(--primary)] text-[var(--on-primary)]" : i < step ? "border-[var(--text-primary)] text-[var(--text-primary)]" : "border-[var(--border-default)]"}`}>
                  {i < step ? <Check size={11} /> : i + 1}
                </span>
                <span className="hidden md:inline">{s.label}</span>
              </button>
              {i < steps.length - 1 && <div className="flex-1 h-px bg-[var(--border-default)] pointer-events-none hidden sm:block" />}
            </div>
          ))}
        </div>
      </div>

      <div className={`mx-auto px-4 md:px-8 py-8 md:py-16 pb-28 md:pb-16 ${step === 3 ? "max-w-[1200px]" : "max-w-[720px]"}`}>
        <div className="flex items-center justify-between">
          <div className="text-overline">Step {step + 1} of {steps.length}</div>
          {statusLabel ? <div className="text-xs text-[var(--text-secondary)] font-mono">{statusLabel}</div> : null}
        </div>
        <h1 className="font-heading text-2xl md:text-4xl tracking-tight font-medium mt-2">
          {step === 0 && "What are you organising?"}
          {step === 1 && "How should it look?"}
          {step === 2 && "What are you selling?"}
          {step === 3 && "What are you collecting?"}
          {step === 4 && "Ready to publish?"}
        </h1>

        <div className="mt-10 space-y-8">
          {step === 0 && <Basics draft={draft} setDraft={setDraft} onSlugAvailabilityChange={setSlugAvailable} />}
          {step === 1 && <Branding draft={draft} setDraft={setDraft} />}
          {step === 2 && <Tickets draft={draft} setDraft={setDraft} />}
          {step === 3 && <Form draft={draft} setDraft={setDraft} />}
          {step === 4 && <Review draft={draft} />}
        </div>

        <div className="fixed bottom-0 inset-x-0 md:static bg-[var(--bg-base-alpha95)] backdrop-blur-md border-t border-[var(--border-default)] p-4 md:p-0 md:mt-16 md:pt-6 md:bg-transparent md:backdrop-blur-none flex items-center justify-between z-30">
          <button
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="btn-ghost disabled:opacity-30"
            data-testid="wizard-back"
          >
            <ChevronLeft size={14} /> Back
          </button>
          {step < steps.length - 1 ? (
            <div className="flex items-center gap-3">
              {step === 2 ? (
                <button type="button" className="btn-ghost" onClick={skipTicketsStep} data-testid="wizard-skip-tickets">
                  Skip for now
                </button>
              ) : null}
              <button
                onClick={() => setStep((s) => s + 1)}
                className="btn-primary"
                data-testid="wizard-next"
                disabled={step === 0 && (!String(draft.title || "").trim() || !slugAvailable)}
              >
                Continue <ChevronRight size={14} />
              </button>
            </div>
          ) : (
            <button
              onClick={publishEvent}
              className="btn-primary"
              disabled={publishing}
              data-testid="wizard-publish"
            >
              {publishing ? "Publishing..." : "Publish event"} <Check size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="text-overline">{label}</label>
      <div className="mt-2">{children}</div>
      {hint && <div className="text-xs text-[var(--text-tertiary)] mt-2 font-mono">{hint}</div>}
    </div>
  );
}

function Basics({ draft, setDraft, onSlugAvailabilityChange }) {
  const [slugStatus, setSlugStatus] = useState("idle");
  const previewSlug = useMemo(() => {
    const fromSlug = slugifyText(draft.slug);
    if (fromSlug) return fromSlug;
    return slugifyText(draft.title) || "your-slug";
  }, [draft.slug, draft.title]);
  const publicUrl = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return origin ? `${origin}/e/${previewSlug}` : `/e/${previewSlug}`;
  }, [previewSlug]);

  useEffect(() => {
    const slug = previewSlug;
    if (!slug || slug === "your-slug") {
      setSlugStatus("idle");
      onSlugAvailabilityChange?.(true);
      return;
    }
    let cancelled = false;
    setSlugStatus("checking");
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/admin/events/slug-available?slug=${encodeURIComponent(slug)}`
        );
        const body = res.ok ? await res.json() : { available: false };
        if (cancelled) return;
        const available = Boolean(body?.available);
        setSlugStatus(available ? "available" : "unavailable");
        onSlugAvailabilityChange?.(available);
      } catch {
        if (!cancelled) {
          setSlugStatus("idle");
          onSlugAvailabilityChange?.(true);
        }
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [previewSlug, onSlugAvailabilityChange]);

  const onChange = (key, value) => {
    if (key === "title") {
      const nextTitle = String(value || "");
      setDraft((prev) => {
        if (prev.slugLocked) return { ...prev, title: nextTitle };
        const generated = slugifyText(nextTitle);
        return { ...prev, title: nextTitle, slug: generated };
      });
      return;
    }
    if (key === "slug") {
      const raw = String(value || "");
      const normalized = slugifyText(raw);
      setDraft((prev) => {
        if (!normalized) {
          return { ...prev, slug: slugifyText(prev.title || ""), slugLocked: false };
        }
        return { ...prev, slug: normalized, slugLocked: true };
      });
      return;
    }
    setDraft((prev) => ({ ...prev, [key]: value }));
  };
  const venueFirstLine = () => {
    setDraft((prev) => {
      const raw = String(prev.venue || "");
      const first = raw.split(/\r?\n/).map((l) => l.trim()).find(Boolean) || "";
      return { ...prev, venue: first };
    });
  };
  return (
    <div className="space-y-6">
      <p className="text-sm text-[var(--text-secondary)] -mt-4">
        Public event page:{" "}
        <a href={publicUrl} className="font-mono text-xs text-[var(--primary)] break-all hover:underline" target="_blank" rel="noreferrer">
          {publicUrl}
        </a>
      </p>
      <Field label="Event title">
        <input className="input-dark" value={draft.title || ""} onChange={(e) => onChange("title", e.target.value)} />
      </Field>
      <Field label="Format">
        <select className="input-dark" value={draft.format || "In-person"} onChange={(e) => onChange("format", e.target.value)}>
          <option>In-person</option>
          <option>Virtual</option>
          <option>Hybrid</option>
          <option>Private</option>
        </select>
      </Field>
      <Field label="Public slug" hint="Used in your event URL path after /e/.">
        <div className="flex">
          <span className="px-3 flex items-center bg-[var(--bg-elev)] border border-r-0 border-[var(--border-default)] text-[var(--text-tertiary)] font-mono text-xs">/e/</span>
          <input className="input-dark" placeholder="stride-2026" value={draft.slug || ""} onChange={(e) => onChange("slug", e.target.value)} />
        </div>
        {slugStatus === "checking" ? (
          <div className="text-xs text-[var(--text-tertiary)] mt-2 font-mono">Checking availability…</div>
        ) : null}
        {slugStatus === "unavailable" ? (
          <div className="text-xs text-[var(--error)] mt-2 font-mono">Unavailable — this slug is already in use.</div>
        ) : null}
        {slugStatus === "available" && previewSlug !== "your-slug" ? (
          <div className="text-xs text-[var(--primary)] mt-2 font-mono">Available</div>
        ) : null}
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Start date" hint="Defaults to today; adjust to your schedule.">
          <input type="date" className="input-dark" value={draft.startDate} onChange={(e) => onChange("startDate", e.target.value)} />
        </Field>
        <Field label="End date (optional)" hint="Leave blank for a single-day event.">
          <input type="date" className="input-dark" value={draft.endDate || ""} onChange={(e) => onChange("endDate", e.target.value)} />
        </Field>
      </div>
      <Field label="Timezone">
        <select className="input-dark" value={draft.timezone} onChange={(e) => onChange("timezone", e.target.value)}>
          {ALL_TIMEZONES.map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </select>
      </Field>
      <Field
        label="Venue"
        hint="Paste a Google Maps address block if you like—use “First line only” to keep just the street line."
      >
        <textarea
          className="input-dark min-h-[88px] font-mono text-sm"
          placeholder={"e.g. Pier 17, 89 South St\nNew York, NY 10038"}
          value={draft.venue || ""}
          onChange={(e) => onChange("venue", e.target.value)}
        />
        <div className="mt-2 flex flex-wrap gap-2">
          <button type="button" className="btn-secondary text-xs" onClick={venueFirstLine}>
            Use first line only
          </button>
        </div>
      </Field>
      <Field label="Capacity">
        <input className="input-dark" type="number" value={draft.capacity} onChange={(e) => onChange("capacity", Number(e.target.value) || 0)} />
      </Field>
    </div>
  );
}

function Branding({ draft, setDraft }) {
  const onChange = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));
  // Upload to the same backend endpoint EventBasicsManager uses, so the
  // wizard, the per-event basics editor, and the rich-text editor all share
  // one image pipeline (Cloudinary if configured, /uploads otherwise).
  // Storing a base64 data URL here as we did before bloated the events row
  // and the URL was silently dropped at create time anyway.
  const uploadAndAssign = (file, key) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}/api/admin/uploads/image`, true);
    try {
      const raw = localStorage.getItem("aivoryems.auth.session");
      const token = raw ? JSON.parse(raw)?.token : "";
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    } catch {
      /* no-op */
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText || "{}");
          if (!data?.url) {
            toast.error("Upload response missing url");
            return;
          }
          let url = data.url;
          if (!url.startsWith("http")) {
            const origin = (typeof window !== "undefined" && window.location?.origin) || "";
            url = `${origin}${url}`;
          }
          onChange(key, url);
        } catch {
          toast.error("Upload response not JSON");
        }
      } else {
        let msg = `Upload failed (${xhr.status})`;
        try {
          const j = JSON.parse(xhr.responseText || "{}");
          if (typeof j?.detail === "string") msg = j.detail;
          else if (Array.isArray(j?.detail) && j.detail[0]?.msg) msg = j.detail[0].msg;
        } catch {
          /* keep default */
        }
        toast.error(msg);
      }
    };
    xhr.onerror = () => toast.error("Upload network error");
    xhr.send(fd);
  };
  return (
    <div className="space-y-8">
      <Field label="Logo">
        <div className="border-2 border-dashed border-[var(--border-default)] p-6 text-center hover:border-[var(--primary)] transition">
          {draft.logoUrl ? <img src={draft.logoUrl} alt="logo" className="h-16 mx-auto mb-3 object-contain" /> : null}
          <div className="text-sm text-[var(--text-secondary)] mb-3">Upload logo · SVG/PNG up to 2MB</div>
          <input type="file" accept="image/*" className="input-dark" onChange={(e) => uploadAndAssign(e.target.files?.[0], "logoUrl")} />
        </div>
      </Field>
      <Field label="Cover image">
        <div className="space-y-3">
          {draft.cover ? <img src={draft.cover} alt="cover" className="w-full h-40 object-cover border border-[var(--border-default)]" /> : null}
          <input type="file" accept="image/*" className="input-dark" onChange={(e) => uploadAndAssign(e.target.files?.[0], "cover")} />
        </div>
      </Field>
      <Field label="Primary brand color">
        <div className="flex flex-wrap gap-3 items-center">
          {["var(--primary)", "#E25D3A", "#60A5FA", "#A78BFA", "#FAFAFA"].map((c) => (
            <button key={c} type="button" onClick={() => onChange("accent", c)} className={`w-12 h-12 border-2 transition ${String(draft.accent || "").toLowerCase() === c.toLowerCase() ? "border-[var(--text-primary)]" : "border-[var(--border-default)] hover:border-[var(--text-primary)]"}`} style={{ background: c }} />
          ))}
          <input type="color" className="w-12 h-12 border border-[var(--border-default)] bg-transparent p-0" value={/^#[0-9A-Fa-f]{6}$/.test(draft.accent || "") ? draft.accent : "#FF5608"} onChange={(e) => onChange("accent", e.target.value)} />
          <input className="input-dark max-w-[160px] font-mono" value={draft.accent || "var(--primary)"} onChange={(e) => onChange("accent", e.target.value)} placeholder="var(--primary)" />
        </div>
      </Field>
      <Field label="Secondary brand color" hint="Optional—used where templates support a second accent (badges, emails, etc.).">
        <div className="flex flex-wrap gap-3 items-center">
          {["#27272A", "#71717A", "#E25D3A", "#60A5FA", "#A78BFA"].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onChange("secondaryAccent", c)}
              className={`w-10 h-10 border-2 transition ${String(draft.secondaryAccent || "").toLowerCase() === c.toLowerCase() ? "border-[var(--text-primary)]" : "border-[var(--border-default)] hover:border-[var(--text-primary)]"}`}
              style={{ background: c }}
            />
          ))}
          <input
            type="color"
            className="w-10 h-10 border border-[var(--border-default)] bg-transparent p-0"
            value={draft.secondaryAccent || "#71717A"}
            onChange={(e) => onChange("secondaryAccent", e.target.value)}
          />
          <input
            className="input-dark max-w-[160px] font-mono"
            value={draft.secondaryAccent || ""}
            onChange={(e) => onChange("secondaryAccent", e.target.value)}
            placeholder="#71717A or leave blank"
          />
        </div>
      </Field>
      <Field label="Email sender name"><input className="input-dark" value={draft.senderName || ""} onChange={(e) => onChange("senderName", e.target.value)} placeholder="Aivory Events" /></Field>
      <Field label="Reply-to address"><input className="input-dark" value={draft.replyTo || ""} onChange={(e) => onChange("replyTo", e.target.value)} placeholder="hello@yourdomain.com" /></Field>
    </div>
  );
}

function Tickets({ draft, setDraft }) {
  const tiers = Array.isArray(draft?.tickets) ? draft.tickets : [];
  const tracks = Array.isArray(draft?.tracks) ? draft.tracks : [];
  const updateTier = (id, patch) =>
    setDraft((prev) => ({
      ...prev,
      tickets: (Array.isArray(prev.tickets) ? prev.tickets : []).map((row) => (row.id === id ? { ...row, ...patch } : row)),
    }));
  const removeTier = (id) =>
    setDraft((prev) => ({ ...prev, tickets: (Array.isArray(prev.tickets) ? prev.tickets : []).filter((row) => row.id !== id) }));
  const addTier = () =>
    setDraft((prev) => ({
      ...prev,
      tickets: [
        ...(Array.isArray(prev.tickets) ? prev.tickets : []),
        { id: `t_${Date.now()}`, name: "New tier", price: 0, quota: 100, status: "Selling", purchaseLimitPerEmail: 1 },
      ],
    }));
  const updateTrack = (id, patch) =>
    setDraft((prev) => ({
      ...prev,
      tracks: (Array.isArray(prev.tracks) ? prev.tracks : []).map((row) => (row.id === id ? { ...row, ...patch } : row)),
    }));
  const removeTrack = (id) =>
    setDraft((prev) => ({ ...prev, tracks: (Array.isArray(prev.tracks) ? prev.tracks : []).filter((row) => row.id !== id) }));
  const addTrack = () =>
    setDraft((prev) => ({
      ...prev,
      tracks: [
        ...(Array.isArray(prev.tracks) ? prev.tracks : []),
        {
          id: `tr_${Date.now()}`,
          name: "New track",
          trackCode: "",
          price: 0,
          quota: 200,
          status: "Selling",
          purchaseLimitPerEmail: 1,
        },
      ],
    }));
  return (
    <div className="space-y-8">
      <div className="surface p-5 space-y-3 border border-[var(--border-default)] border-dashed">
        <div className="text-overline">Registration approval</div>
        <p className="text-xs text-[var(--text-secondary)]">
          This applies to every registration for this event. Fine-tune later under{" "}
          <span className="text-[var(--text-primary)]">Event basics</span> or the Tickets area after publish.
        </p>
        <select
          className="input-dark max-w-md"
          value={draft?.registrationApproval || "auto_confirm"}
          onChange={(e) => setDraft((prev) => ({ ...prev, registrationApproval: e.target.value }))}
        >
          <option value="auto_confirm">Auto confirm registration immediately</option>
          <option value="manual_approval">Holding list — manual approval required</option>
        </select>
      </div>

      <div>
        <div>
          <div className="text-overline">Ticket tiers</div>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Conference passes, GA, VIP, etc.</p>
        </div>
        <div className="mt-4 grid md:grid-cols-6 gap-3 text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)] px-2">
          <div>Name</div>
          <div>Price</div>
          <div>Quota</div>
          <div>Status</div>
          <div>Per-email limit</div>
          <div> </div>
        </div>
        {tiers.map((t) => (
          <div key={t.id} className="surface p-5 mt-2">
            <div className="grid md:grid-cols-6 gap-3">
              <input className="input-dark" value={t.name || ""} onChange={(e) => updateTier(t.id, { name: e.target.value })} />
              <input className="input-dark" type="number" min="0" value={Number(t.price || 0)} onChange={(e) => updateTier(t.id, { price: Number(e.target.value || 0) })} />
              <input className="input-dark" type="number" min="0" value={Number(t.quota || 0)} onChange={(e) => updateTier(t.id, { quota: Number(e.target.value || 0) })} />
              <select className="input-dark" value={t.status || "Selling"} onChange={(e) => updateTier(t.id, { status: e.target.value })}>
                <option>Selling</option>
                <option>Draft</option>
                <option>Sold out</option>
                <option>Hidden</option>
              </select>
              <input
                className="input-dark"
                type="number"
                min="0"
                value={Number(t.purchaseLimitPerEmail ?? 0)}
                onChange={(e) => updateTier(t.id, { purchaseLimitPerEmail: Math.max(0, Number(e.target.value || 0)) })}
              />
              <button type="button" className="btn-ghost text-xs text-[var(--error)] justify-self-end" onClick={() => removeTier(t.id)}>
                Remove
              </button>
            </div>
          </div>
        ))}
        <button className="btn-secondary w-full justify-center mt-3" type="button" onClick={addTier}>
          + Add tier
        </button>
        <div className="text-xs text-[var(--text-tertiary)] font-mono mt-2">Per-email limit 0 = unlimited. You can add add-ons and tracks in Tickets after publishing.</div>
      </div>

      <div>
        <div className="text-overline">Tracks</div>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          Optional programmes inside the same event (e.g. “AI track”, “Marketing track”). Leave empty for a single programme or configure later in Tickets.
        </p>
        <div className="mt-4 grid md:grid-cols-7 gap-3 text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)] px-2">
          <div>Track name</div>
          <div>Code</div>
          <div>Price</div>
          <div>Quota</div>
          <div>Status</div>
          <div>Per-email</div>
          <div> </div>
        </div>
        {tracks.map((tr) => (
          <div key={tr.id} className="surface p-5 mt-2">
            <div className="grid md:grid-cols-7 gap-3">
              <input className="input-dark" value={tr.name || ""} onChange={(e) => updateTrack(tr.id, { name: e.target.value })} />
              <input
                className="input-dark font-mono text-xs"
                placeholder="AI"
                value={tr.trackCode || ""}
                onChange={(e) => updateTrack(tr.id, { trackCode: e.target.value })}
              />
              <input className="input-dark" type="number" min="0" value={Number(tr.price ?? 0)} onChange={(e) => updateTrack(tr.id, { price: Number(e.target.value || 0) })} />
              <input className="input-dark" type="number" min="0" value={Number(tr.quota ?? 0)} onChange={(e) => updateTrack(tr.id, { quota: Number(e.target.value || 0) })} />
              <select className="input-dark" value={tr.status || "Selling"} onChange={(e) => updateTrack(tr.id, { status: e.target.value })}>
                <option>Selling</option>
                <option>Draft</option>
                <option>Sold out</option>
                <option>Hidden</option>
              </select>
              <input
                className="input-dark"
                type="number"
                min="0"
                value={Number(tr.purchaseLimitPerEmail ?? 1)}
                onChange={(e) => updateTrack(tr.id, { purchaseLimitPerEmail: Math.max(0, Number(e.target.value || 0)) })}
              />
              <button type="button" className="btn-ghost text-xs text-[var(--error)] justify-self-end" onClick={() => removeTrack(tr.id)}>
                Remove
              </button>
            </div>
          </div>
        ))}
        <button className="btn-secondary w-full justify-center mt-3" type="button" onClick={addTrack}>
          + Add track
        </button>
      </div>
    </div>
  );
}

function FormCanvasPreview({ field }) {
  const f = field || {};
  const t = String(f.type || "short_answer");
  if (t === "section") return <div className="mt-2 text-sm text-[var(--text-tertiary)] border-l-2 border-[var(--border-default)] pl-3">Section</div>;
  if (t === "checkbox_group" || t === "select" || t === "multiselect" || t === "dropdown")
    return (
      <select className="mt-2 w-full bg-transparent border-b border-[var(--border-default)] py-2 text-sm focus:outline-none">
        {(f.options || ["Option 1"]).map((o) => (
          <option key={o}>{o}</option>
        ))}
      </select>
    );
  if (t === "radio" || t === "yes_no")
    return (
      <div className="mt-2 space-y-1">
        {(f.options || ["Option 1", "Option 2"]).map((o) => (
          <label key={o} className="flex items-center gap-2 text-xs">
            <input type="radio" name={`wiz-${f.id}`} readOnly />
            {o}
          </label>
        ))}
      </div>
    );
  if (t === "checkbox") return <label className="flex items-center gap-2 mt-2 text-sm"><input type="checkbox" readOnly /> {f.label}</label>;
  if (t === "consent")
    return (
      <label className="flex items-start gap-2 mt-2 text-xs">
        <input type="checkbox" className="mt-0.5" readOnly /> I agree to the terms.
      </label>
    );
  if (t === "textarea" || t === "long_answer" || t === "paragraph")
    return <textarea className="mt-2 w-full bg-transparent border-b border-[var(--border-default)] py-2 text-sm focus:outline-none" rows={2} readOnly />;
  if (t === "file") return <input type="file" className="mt-2 w-full text-xs" disabled />;
  if (t === "hidden") return <div className="mt-2 text-xs text-[var(--text-tertiary)] font-mono">Hidden field</div>;
  if (t === "country")
    return (
      <select className="mt-2 w-full bg-transparent border-b border-[var(--border-default)] py-2 text-sm focus:outline-none">
        {(f.options || ["Singapore"]).map((o) => (
          <option key={o}>{o}</option>
        ))}
      </select>
    );
  return (
    <input
      type={t === "email" ? "email" : t === "phone" ? "tel" : t === "number" ? "number" : t === "url" ? "url" : t === "date" ? "date" : t === "time" ? "time" : t === "datetime" ? "datetime-local" : "text"}
      className="mt-2 w-full bg-transparent border-b border-[var(--border-default)] py-2 text-sm focus:outline-none"
      readOnly
    />
  );
}

function Form({ draft, setDraft }) {
  const fs = Array.isArray(draft?.formFields) ? draft.formFields : [];
  const [activeId, setActiveId] = useState(fs[0]?.id || null);
  const activeField = useMemo(() => fs.find((f) => f.id === activeId) || null, [fs, activeId]);

  useEffect(() => {
    if (fs.length && !fs.some((f) => f.id === activeId)) {
      setActiveId(fs[0].id);
    }
  }, [fs, activeId]);

  const updateField = (id, patch) =>
    setDraft((prev) => ({
      ...prev,
      formFields: (Array.isArray(prev.formFields) ? prev.formFields : []).map((row) => (row.id === id ? { ...row, ...patch } : row)),
    }));
  const removeField = (id) => {
    setDraft((prev) => {
      const next = (Array.isArray(prev.formFields) ? prev.formFields : []).filter((row) => row.id !== id);
      setActiveId((cur) => (cur === id ? next[0]?.id || null : cur));
      return { ...prev, formFields: next };
    });
  };
  const moveField = (id, delta) => {
    setDraft((prev) => {
      const list = [...(Array.isArray(prev.formFields) ? prev.formFields : [])];
      const i = list.findIndex((x) => x.id === id);
      if (i < 0) return prev;
      const j = i + delta;
      if (j < 0 || j >= list.length) return prev;
      const tmp = list[i];
      list[i] = list[j];
      list[j] = tmp;
      return { ...prev, formFields: list };
    });
  };
  const addField = (type = "short_answer") => {
    setDraft((prev) => {
      const list = Array.isArray(prev.formFields) ? prev.formFields : [];
      const nf = createDefaultField(type, list.length);
      setActiveId(nf.id);
      return { ...prev, formFields: [...list, nf] };
    });
  };

  const update = (key, val) => {
    if (!activeField) return;
    updateField(activeField.id, { [key]: val });
  };

  return (
    <div className="space-y-4" data-testid="wizard-form-step">
      <p className="text-sm text-[var(--text-secondary)] -mt-2">
        Same layout pattern as <span className="text-[var(--text-primary)]">Form Builder</span>: add fields from the library, arrange them on the canvas, then edit details in Properties. Conditional logic and publishing live in the full Form Builder
        after you create the event.
      </p>
      <div className="flex-1 grid grid-cols-1 md:grid-cols-12 min-h-[420px] md:min-h-[520px] border border-[var(--border-default)] bg-[var(--bg-elev)] overflow-hidden rounded-sm">
        <div className="col-span-12 md:col-span-3 border-r border-[var(--border-default)] overflow-y-auto p-4">
          <div className="text-overline mb-3">Field library</div>
          <div className="space-y-1">
            {FIELD_LIBRARY.map((f, idx) => (
              <button
                key={`${f.type}-${idx}`}
                type="button"
                onClick={() => addField(f.type)}
                className="w-full text-left flex items-center justify-between px-3 py-2.5 surface-hover border border-transparent hover:border-[var(--border-default)] text-sm group"
              >
                <span>{f.label}</span>
                <Plus size={14} className="text-[var(--text-tertiary)] group-hover:text-[var(--primary)]" />
              </button>
            ))}
          </div>
        </div>
        <div className="col-span-12 md:col-span-6 overflow-y-auto bg-[var(--bg-elev)] dot-grid p-8">
          <div className="max-w-md mx-auto bg-[var(--bg-elev)] text-[var(--text-primary)] shadow-[0_30px_60px_rgba(0,0,0,0.5)] p-8 border border-[var(--border-default)]">
            <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">Registration</div>
            <div className="font-heading text-2xl mt-1 tracking-tight">What are you collecting?</div>
            <div className="mt-6 space-y-5">
              {fs.map((f) => (
                <div
                  key={f.id}
                  onClick={() => setActiveId(f.id)}
                  className={`group cursor-pointer relative ${activeId === f.id ? "ring-2 ring-[var(--primary)] ring-offset-4 ring-offset-[#FAFAFA]" : ""}`}
                >
                  <div className="text-[11px] uppercase tracking-widest text-[var(--text-tertiary)] font-medium flex items-center justify-between">
                    <span>{f.label || "Untitled"}</span>
                    {f.required && <span className="text-[var(--error)] ml-1">*</span>}
                  </div>
                  <FormCanvasPreview field={f} />
                </div>
              ))}
            </div>
            <button type="button" className="mt-8 w-full bg-[var(--bg-base)] text-[var(--primary)] py-3 text-sm font-medium tracking-wide opacity-50 cursor-default">
              Continue
            </button>
          </div>
        </div>
        <div className="col-span-12 md:col-span-3 border-l border-[var(--border-default)] overflow-y-auto p-4">
          <div className="flex items-center gap-2 text-overline mb-4">
            <Settings2 size={11} /> Properties
          </div>
          {activeField ? (
            <div className="space-y-4">
              <div>
                <label className="text-overline">Label</label>
                <input className="input-dark mt-2" value={activeField.label || ""} onChange={(e) => update("label", e.target.value)} />
              </div>
              <div>
                <label className="text-overline">Field key</label>
                <input className="input-dark mt-2 font-mono text-xs" value={activeField.key || activeField.id || ""} onChange={(e) => update("key", e.target.value)} />
              </div>
              <div>
                <label className="text-overline">Type</label>
                <select className="input-dark mt-2" value={activeField.type || "short_answer"} onChange={(e) => update("type", e.target.value)}>
                  {FIELD_LIBRARY.map((item) => (
                    <option key={item.type} value={item.type}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="surface p-3 flex items-center justify-between">
                <span className="text-sm">Required</span>
                <button
                  type="button"
                  onClick={() => update("required", !activeField.required)}
                  className={`w-9 h-5 transition relative ${activeField.required ? "bg-[var(--primary)]" : "bg-[var(--border-default)]"}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-[var(--bg-base)] transition-all ${activeField.required ? "left-4" : "left-0.5"}`} />
                </button>
              </div>
              <div>
                <label className="text-overline">Placeholder</label>
                <input className="input-dark mt-2" value={activeField.placeholder || ""} onChange={(e) => update("placeholder", e.target.value)} />
              </div>
              <div>
                <label className="text-overline">Help text</label>
                <textarea className="input-dark mt-2" rows={3} value={activeField.helpText || ""} onChange={(e) => update("helpText", e.target.value)} />
              </div>
              {OPTION_TYPES.has(String(activeField.type || "")) ? (
                <div>
                  <label className="text-overline">Options (one per line)</label>
                  <textarea
                    className="input-dark mt-2"
                    rows={5}
                    value={(activeField.options || []).join("\n")}
                    onChange={(e) =>
                      update(
                        "options",
                        e.target.value
                          .split("\n")
                          .map((x) => x.trim())
                          .filter(Boolean),
                      )
                    }
                  />
                </div>
              ) : null}
              <div className="flex items-center gap-2 pt-2 border-t border-[var(--border-default)]">
                <GripVertical size={14} className="text-[var(--text-tertiary)]" />
                <button type="button" className="btn-ghost p-1" aria-label="Move up" onClick={() => moveField(activeField.id, -1)}>
                  <ChevronUp size={14} />
                </button>
                <button type="button" className="btn-ghost p-1" aria-label="Move down" onClick={() => moveField(activeField.id, 1)}>
                  <ChevronDown size={14} />
                </button>
                <button type="button" className="btn-ghost p-1 text-[var(--error)] ml-auto" aria-label="Remove field" onClick={() => removeField(activeField.id)}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ) : (
            <div className="text-xs text-[var(--text-tertiary)]">Select a field on the canvas or add one from the library.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Review({ draft }) {
  const title = String(draft?.title || "Untitled event").trim() || "Untitled event";
  const venue = String(draft?.venue || "TBD").trim() || "TBD";
  const dateLabel = formatEventDateRange(draft?.startDate, draft?.endDate) || "—";
  const timezone = String(draft?.timezone || "UTC");
  const capacity = Number(draft?.capacity || 0).toLocaleString();
  const slug = String(draft?.slug || "").trim() || "event";
  const ticketCount = Array.isArray(draft?.tickets) ? draft.tickets.length : 0;
  const trackCount = Array.isArray(draft?.tracks) ? draft.tracks.length : 0;
  const fieldCount = Array.isArray(draft?.formFields) ? draft.formFields.length : 0;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const publicUrl = origin ? `${origin}/e/${slug}` : `/e/${slug}`;
  return (
    <div className="space-y-3">
      {[
        ["Event", `${title} · ${venue}`],
        ["Dates", `${dateLabel} · ${timezone}`],
        ["Format", String(draft?.format || "In-person")],
        ["Registration policy", draft?.registrationApproval === "manual_approval" ? "Holding list (manual approval)" : "Auto confirm"],
        ["Capacity", capacity],
        ["Tiers & tracks", `${ticketCount} tier${ticketCount === 1 ? "" : "s"} · ${trackCount} track${trackCount === 1 ? "" : "s"}`],
        ["Form fields", `${fieldCount}`],
        ["Public page", publicUrl],
      ].map(([k, v]) => (
        <div key={k} className="flex items-center justify-between border-b border-dashed border-[var(--border-default)] py-3 text-sm">
          <span className="text-[var(--text-tertiary)] font-mono uppercase text-[11px] tracking-widest">{k}</span>
          <span className="text-[var(--text-primary)]">{v}</span>
        </div>
      ))}
    </div>
  );
}
